import type { FC, KeyboardEvent, ReactNode } from 'react'
import type { SearchResult } from '@/types/content'
import type { SearchResponse } from '@/lib/server/content/search'

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useRouter } from 'next/router'
import Link from 'next/link'

import {
    ArrowRight,
    BookOpen,
    Clapperboard,
    Disc3,
    Feather,
    LibraryBig,
    LoaderCircle,
    Music2,
    Search,
    X
} from 'lucide-react'

import { cms } from '@/lib/cms'
import { cn } from '@/lib/utils'
import { Modal } from '@/components/kit/modal'

interface SearchApi {
    openSearch: () => void
}

const SearchContext = createContext<SearchApi>({ openSearch: () => undefined })

export const useSearch = (): SearchApi => useContext(SearchContext)

const groupMeta: Array<{ key: keyof SearchResponse['groups'], label: string, Icon: typeof Music2 }> = [
    { key: 'songs', label: 'Songs', Icon: Music2 },
    { key: 'lyrics', label: 'In the lyrics', Icon: BookOpen },
    { key: 'albums', label: 'Albums & EPs', Icon: Disc3 },
    { key: 'videos', label: 'Videos', Icon: Clapperboard },
    { key: 'journal', label: 'Poetry & journal', Icon: Feather },
    { key: 'collections', label: 'Collections', Icon: LibraryBig }
]

const suggestions = [
    { href: '/emotions', label: 'Find music by emotion' },
    { href: '/lyrics', label: 'Browse every lyric' },
    { href: '/journal', label: 'Read the poetry' },
    { href: '/videos', label: 'Watch the films' }
]

/** Wraps the part of the text that matches the query in a highlight. */
export const Highlight: FC<{ text: string, term: string }> = ({ text, term }): ReactNode => {
    const index = term ? text.toLowerCase().indexOf(term.toLowerCase()) : -1

    if (index < 0) return <>{text}</>

    return (
        <>
            {text.slice(0, index)}
            <mark className='rounded-sm bg-gold/25 px-0.5 text-inherit'>{text.slice(index, index + term.length)}</mark>
            {text.slice(index + term.length)}
        </>
    )
}

const SearchPanel: FC<{ onClose: () => void }> = ({ onClose }): ReactNode => {
    const router = useRouter()
    const inputRef = useRef<HTMLInputElement>(null)
    const [term, setTerm] = useState('')
    const [state, setState] = useState<'idle' | 'loading' | 'done' | 'error'>('idle')
    const [data, setData] = useState<SearchResponse | null>(null)
    const [active, setActive] = useState(0)

    useEffect(() => {
        inputRef.current?.focus()
    }, [])

    useEffect(() => {
        const value = term.trim()

        if (value.length < 2) {
            setState('idle')
            setData(null)

            return
        }

        const controller = new AbortController()
        const timer = window.setTimeout(async () => {
            setState('loading')

            try {
                const response = await fetch(`/api/search?q=${encodeURIComponent(value)}&limit=4`, { signal: controller.signal })
                const payload = await response.json()

                if (!response.ok || !payload.ok) throw new Error(payload.error ?? 'Search failed')

                setData(payload.data as SearchResponse)
                setActive(0)
                setState('done')
            }
            catch (error) {
                if ((error as Error).name !== 'AbortError') setState('error')
            }
        }, 220)

        return () => {
            controller.abort()
            window.clearTimeout(timer)
        }
    }, [term])

    const flat = useMemo<Array<SearchResult>>(() => {
        if (!data) return []

        return groupMeta.flatMap(group => data.groups[group.key])
    }, [data])

    const go = useCallback((href: string) => {
        onClose()
        router.push(href)
    }, [onClose, router])

    const allHref = `/search?q=${encodeURIComponent(term.trim())}`

    const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'ArrowDown') {
            event.preventDefault()
            setActive(index => Math.min(flat.length - 1, index + 1))
        }

        if (event.key === 'ArrowUp') {
            event.preventDefault()
            setActive(index => Math.max(0, index - 1))
        }

        if (event.key === 'Enter') {
            event.preventDefault()

            if (flat[active]) go(flat[active].href)
            else if (term.trim().length >= 2) go(allHref)
        }
    }

    let cursor = -1

    return (
        <div className='mx-auto mt-[8vh] w-[calc(100vw-1.5rem)] max-w-2xl overflow-hidden rounded-xl border border-white/10 bg-[#0e0e0e]/95 shadow-2xl shadow-black/70 backdrop-blur-xl sm:mt-[12vh]'>
            <div className='flex items-center gap-3 border-b border-white/[0.07] px-5'>
                <Search className='size-5 shrink-0 text-gold' aria-hidden='true' />

                <label htmlFor='site-search' className='sr-only'>
                    Search the site
                </label>

                <input
                    ref={inputRef}
                    id='site-search'
                    type='search'
                    value={term}
                    onChange={event => setTerm(event.target.value)}
                    onKeyDown={onKeyDown}
                    placeholder='Search songs, lyrics, albums, videos and writings'
                    autoComplete='off'
                    spellCheck={false}
                    role='combobox'
                    aria-expanded={flat.length > 0}
                    aria-controls='site-search-results'
                    aria-activedescendant={flat[active] ? `search-option-${active}` : undefined}
                    className='h-16 w-full bg-transparent text-base text-ivory placeholder:text-ivory/35 focus:outline-none'
                />

                {state === 'loading' && <LoaderCircle className='size-4 shrink-0 animate-spin text-ivory/50' aria-hidden='true' />}

                <button type='button' onClick={onClose} className='rounded-full p-2 text-ivory/50 transition hover:bg-white/5 hover:text-ivory' aria-label='Close search'>
                    <X className='size-4' aria-hidden='true' />
                </button>
            </div>

            <div id='site-search-results' role='listbox' aria-label='Search results' className='max-h-[60vh] overflow-y-auto p-2'>
                {state === 'idle' && (
                    <div className='p-4'>
                        <p className='eyebrow mb-3 text-ivory/40'>
                            Begin somewhere
                        </p>

                        <ul className='grid gap-1 sm:grid-cols-2'>
                            {suggestions.map(item => (
                                <li key={item.href}>
                                    <Link href={item.href} onClick={onClose} className='group flex items-center justify-between rounded-lg px-3 py-2.5 text-sm text-ivory/75 transition hover:bg-white/[0.04] hover:text-ivory'>
                                        {item.label}
                                        <ArrowRight className='size-4 -translate-x-1 opacity-0 transition group-hover:translate-x-0 group-hover:opacity-100' aria-hidden='true' />
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                {state === 'error' && (
                    <p className='px-4 py-8 text-center text-sm text-ivory/60'>
                        Search is not responding right now. Please try again in a moment.
                    </p>
                )}

                {state === 'done' && data && data.total === 0 && (
                    <div className='px-4 py-10 text-center'>
                        <p className='font-heading text-2xl text-ivory'>
                            Nothing found for &ldquo;{data.query}&rdquo;
                        </p>

                        <p className='mt-2 text-sm text-ivory/55'>
                            Try a single word from a lyric, or a feeling such as &ldquo;longing&rdquo;.
                        </p>
                    </div>
                )}

                {data && data.total > 0 && groupMeta.map(group => {
                    const items = data.groups[group.key]

                    if (items.length === 0) return null

                    return (
                        <div key={group.key} className='py-1'>
                            <p className='eyebrow flex items-center gap-2 px-3 pt-3 pb-2 text-ivory/40'>
                                <group.Icon className='size-3.5' aria-hidden='true' />
                                {group.label}
                            </p>

                            {items.map(item => {
                                cursor += 1

                                const index = cursor

                                return (
                                    <button
                                        key={`${group.key}-${item.id}-${item.language ?? ''}`}
                                        id={`search-option-${index}`}
                                        type='button'
                                        role='option'
                                        aria-selected={index === active}
                                        onMouseEnter={() => setActive(index)}
                                        onClick={() => go(item.href)}
                                        className={cn('flex w-full items-start gap-3 rounded-lg px-3 py-2.5 text-left transition', index === active ? 'bg-white/[0.06]' : 'hover:bg-white/[0.03]')}
                                    >
                                        <span className='min-w-0 flex-1'>
                                            <span className='block truncate font-heading text-lg leading-tight text-ivory'>
                                                <span {...cms(item.table, item.id, 'title')}>{item.title}</span>
                                            </span>

                                            {item.excerpt && (
                                                <span className='mt-0.5 block truncate text-sm text-ivory/55'>
                                                    <Highlight text={item.excerpt} term={data.query} />
                                                </span>
                                            )}
                                        </span>

                                        {item.meta && (
                                            <span className='mt-1 shrink-0 text-[0.65rem] uppercase tracking-[0.16em] text-ivory/35'>
                                                {item.meta}
                                            </span>
                                        )}
                                    </button>
                                )
                            })}
                        </div>
                    )
                })}
            </div>

            {term.trim().length >= 2 && (
                <div className='flex items-center justify-between border-t border-white/[0.07] px-5 py-3 text-xs text-ivory/45'>
                    <span className='hidden sm:inline'>
                        Use the arrow keys and Enter to choose
                    </span>

                    <Link href={allHref} onClick={onClose} className='inline-flex items-center gap-1.5 text-gold transition hover:text-ivory'>
                        See every result
                        <ArrowRight className='size-3.5' aria-hidden='true' />
                    </Link>
                </div>
            )}
        </div>
    )
}

export const SearchProvider: FC<{ children: ReactNode }> = ({ children }): ReactNode => {
    const router = useRouter()
    const [open, setOpen] = useState(false)
    const isAdmin = router.pathname.startsWith('/admin')

    useEffect(() => {
        if (isAdmin) return

        const onKey = (event: KeyboardEvent) => {
            const target = event.target as HTMLElement | null
            const typing = target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)

            if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
                event.preventDefault()
                setOpen(value => !value)
            }
            else if (event.key === '/' && !typing) {
                event.preventDefault()
                setOpen(true)
            }
        }

        window.addEventListener('keydown', onKey)

        return () => window.removeEventListener('keydown', onKey)
    }, [isAdmin])

    useEffect(() => {
        const close = () => setOpen(false)

        router.events.on('routeChangeStart', close)

        return () => router.events.off('routeChangeStart', close)
    }, [router.events])

    const api = useMemo(() => ({ openSearch: () => setOpen(true) }), [])

    return (
        <SearchContext.Provider value={api}>
            {children}

            <Modal open={open} onClose={() => setOpen(false)} label='Search' className='h-full w-full'>
                <SearchPanel onClose={() => setOpen(false)} />
            </Modal>
        </SearchContext.Provider>
    )
}
